/**
 * `info` means something arrived, not an outcome or a problem. It exists for
 * the worker app's chat: a green office reply would say the worker did
 * something right.
 */
export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

export type ToastDurations = {
  success: number;
  error: number;
  warning: number;
  info: number;
};

/**
 * Default time on screen per severity. Lives here, not in `ToastProvider`, so
 * the sites' provider can import it without pulling `Alert`, `IconButton` and
 * theme tokens into the marketing bundles.
 */
export const DEFAULT_TOAST_DURATIONS: ToastDurations = {
  success: 3000,
  error: 5000,
  warning: 5000,
  info: 5000,
};

/**
 * A toast's button. A label and handler, not a node: the apps draw a `Button`,
 * the sites a `SiteButton`, and the hooks raising toasts are `.ts` without JSX.
 */
export type ToastAction = { label: string; onClick: () => void };

export type ErrorToastOptions = {
  action?: ToastAction;
  /**
   * For a message the reader must act on, like screenshotting a timestamp. An
   * `action` implies it: a button gone in five seconds is no recovery path.
   */
  staysUntilClosed?: boolean;
};

/**
 * Unlike an error's, an info action does **not** keep the toast up until
 * closed. The worker app gives `info` twenty seconds, enough for a man holding
 * a shovel; a banner that never leaves would cover his screen. Tapping the
 * action closes it (`useToastController`).
 */
export type InfoToastOptions = { action?: ToastAction };

export type Toast = {
  showSuccess: (message: string, durationMs?: number) => void;
  showError: (message: string, options?: ErrorToastOptions) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string, options?: InfoToastOptions) => void;
};
